<template>
  <div
    role="dialog"
    id="radix-_r_23_"
    aria-describedby="radix-_r_25_"
    aria-labelledby="radix-_r_24_"
    data-state="open"
    class="grid w-full gap-4 p-6 shadow-lg"
    tabindex="-1"
    style="pointer-events: auto"
    v-loading="loading"
  >
    <div class="flex flex-col space-y-1.5 text-center sm:text-left">
      <h2
        id="radix-_r_24_"
        class="text-lg font-semibold leading-none tracking-tight"
      >
        {{ id ? "Edit Alert Rule" : "Create WhatsApp Alert Rule" }}
      </h2>
      <p id="radix-_r_25_" class="text-sm text-muted-foreground">
        Configure a new WhatsApp alert notification rule
      </p>
    </div>
    <div class="space-y-6 py-4">
      <div class="space-y-4">
        <h3 class="text-sm font-semibold">Basic Information</h3>
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            for="name"
          >
            Rule Name
          </label>
          <el-input
            id="name"
            placeholder="e.g., Critical Temperature Alert"
            v-model="form.ruleName"
          />
        </div>
        <div class="space-y-2">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            for="description"
          >
            Description
          </label>
          <el-input
            type="textarea"
            id="description"
            placeholder="Describe when this alert should trigger..."
            v-model="form.description"
          />
        </div>
        <div class="space-y-2 flex flex-col">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Event Source (Select multiple)
          </label>
          <SmartSelect
            v-model="deviceKeys"
            :load-method="getAllAssetVo"
            value-field="deviceKey"
            display-field="name"
            multiple
            placeholder="Select event sources..."
            ref="deviceRef"
            value-type="Array<number>"
          />
        </div>
        <div class="space-y-2 flex flex-col">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Select Level (Select multiple)
          </label>
          <SmartSelect
            v-model="eventLevels"
            :options="[
              { name: 'Low', id: 0 },
              { name: 'Medium', id: 1 },
              { name: 'High', id: 2 },
            ]"
            class="select-width"
            clearable
            placeholder="Select levels..."
            multiple
            value-type="Array<number>"
          />
        </div>
      </div>
      <div class="space-y-4">
        <h3 class="text-sm font-semibold">Cooldown Period</h3>
        <p class="text-sm text-muted-foreground">
          Prevents repeated WhatsApp messages for the same alert within the
          specified time window
        </p>
        <div class="space-y-2 flex flex-col">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            for="cooldown"
          >
            Cooldown Duration
          </label>
          <SmartSelect
            id="cooldown"
            v-model="form.cooldownMin"
            :options="[
              { name: 'No cooldown', id: 0 },
              { name: '5 minutes', id: 5 },
              { name: '10 minutes', id: 10 },
              { name: '15 minutes (Recommended)', id: 15 },
              { name: '30 minutes', id: 30 },
              { name: '1 hour', id: 60 },
              { name: '2 hours', id: 120 },
              { name: '4 hours', id: 240 },
            ]"
            placeholder="Select cooldown duration"
            clearable
          />
          <p class="text-xs text-muted-foreground">
            If the same alert triggers multiple times, WhatsApp messages will
            only be sent once per cooldown period
          </p>
        </div>
      </div>
      <!--暂时不做-->
      <!-- <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-sm font-semibold">Maintenance Schedules</h3>
            <p class="text-sm text-muted-foreground">
              Temporarily pause alerts during maintenance periods
            </p>
          </div>
          <button
            class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-9 rounded-md px-3"
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-plus mr-2 h-4 w-4"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
            Add Schedule
          </button>
        </div>
        <div
          class="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground"
        >
          No maintenance schedules configured. Alerts will trigger normally.
        </div>
      </div> -->
      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      ></div>
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold">Primary Recipients</h3>
            <p class="text-sm text-muted-foreground">
              Users who will receive the initial WhatsApp alert
            </p>
          </div>
        </div>
        <div class="space-y-2 flex flex-col">
          <label
            class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Select Recipients
          </label>
          <SmartSelect
            v-model="recipients"
            :load-method="listNotificationRecipients"
            value-field="id"
            display-field="name"
            multiple
            placeholder="Add recipients..."
            value-type="Array<number>"
          />
        </div>
      </div>
      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      ></div>
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold">Escalation Rules</h3>
            <p class="text-sm text-muted-foreground">
              Configure automatic escalation if alert is not acknowledged
            </p>
          </div>
          <!-- <button
            class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-9 rounded-md px-3"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-plus mr-2 h-4 w-4"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
            Add Level
          </button> -->
        </div>
        <div
          class="rounded-lg border bg-card text-card-foreground shadow-2xs"
          data-v0-t="card"
        >
          <div class="flex flex-col space-y-1.5 p-6 pb-3">
            <div class="flex items-center justify-between">
              <h3 class="font-semibold tracking-tight text-base">
                <div class="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-circle-arrow-up h-5 w-5 text-orange-500"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="m16 12-4-4-4 4"></path>
                    <path d="M12 16V8"></path>
                  </svg>
                  Escalation Level 1
                </div>
              </h3>
            </div>
          </div>
          <div class="p-6 pt-0 space-y-4">
            <div class="grid gap-4 md:grid-cols-2">
              <div class="space-y-2">
                <label
                  class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >Wait Time (minutes)</label
                >
                <el-input-number
                  :min="0"
                  v-model="form.escalationMin"
                  controls-position="right"
                />
                <p class="text-xs text-muted-foreground">
                  Escalate if no acknowledgment within this time
                </p>
              </div>
              <div class="space-y-2">
                <label
                  class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >Escalation Recipients</label
                >
                <SmartSelect
                  v-model="escalationRecipients"
                  :load-method="listNotificationRecipients"
                  value-field="id"
                  display-field="name"
                  multiple
                  placeholder="Select escalation recipients..."
                  value-type="Array<number>"
                />
                <p class="text-xs text-muted-foreground">
                  Choose who should be notified when the alert escalates
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      ></div>
      <div class="space-y-4">
        <h3 class="text-lg font-semibold">Message Template</h3>
        <div class="grid gap-4 md:grid-cols-2">
          <div class="space-y-3">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              for="messageTemplate"
            >
              Template Editor
            </label>
            <el-input
              type="textarea"
              rows="10"
              v-model="form.template"
              id="messageTemplate"
            >
            </el-input>
            <div class="space-y-2">
              <p class="text-xs text-muted-foreground font-medium">
                Available variables:
              </p>
              <div class="flex flex-wrap gap-1.5">
                <div
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-v0-t="badge"
                >
                  ${eventContent}
                </div>
                <div
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-v0-t="badge"
                >
                  ${eventLevel}
                </div>
                <div
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-v0-t="badge"
                >
                  ${deviceKey}
                </div>
                <div
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-v0-t="badge"
                >
                  ${assetName}
                </div>
                <div
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-v0-t="badge"
                >
                  ${ruleName}
                </div>
                <div
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-v0-t="badge"
                >
                  ${eventName}
                </div>
                <div
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-v0-t="badge"
                >
                  ${timestamp}
                </div>
                <div
                  class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-v0-t="badge"
                >
                  ${timestamp:Asia/Hong_Kong}
                </div>
              </div>
              <div class="text-xs text-muted-foreground mt-2">
                Supported timezone:
                <span class="font-mono">${timestamp:Asia/Hong_Kong}</span>,
                default is Hong Kong timezone
              </div>
              <div class="text-xs text-muted-foreground mt-2">
                Example: Alert <span class="font-mono">${eventContent}</span> on
                <span class="font-mono">${assetName}</span> (<span
                  class="font-mono"
                  >${deviceKey}</span
                >) at <span class="font-mono">${timestamp:Asia/Hong_Kong}</span>
              </div>
            </div>
          </div>
          <div class="space-y-3">
            <label
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Message Preview
            </label>
            <div class="p-4 rounded-lg bg-[#1a232a] border border-[#26323a]">
              <div
                class="bg-[#222e35] text-white p-3 rounded-lg rounded-bl-none max-w-[85%] shadow-md"
              >
                <p class="text-sm whitespace-pre-wrap break-words">
                  {{ messagePreview }}
                </p>
                <div class="flex items-center justify-end gap-1 mt-1">
                  <span class="text-[10px] text-white/60">10:30 AM</span>
                </div>
              </div>
            </div>
            <p class="text-xs text-muted-foreground">
              This is how your alert will appear in WhatsApp
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between">
      <div class="flex flex-row">
        <el-switch
          id="enabledSwitch"
          v-model="form.enabled"
          active-text="Enabled"
          inactive-text="Disabled"
        />
      </div>
      <div class="flex flex-row gap-2">
        <button
          @click="$emit('cancel')"
          class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 px-4 py-2"
        >
          Cancel
        </button>
        <el-button :loading="submitting" type="primary" @click="handleSubmit">
          {{ id ? "Save Changes" : "Create Alert Rule" }}
        </el-button>
      </div>
    </div>
    <button
      @click="$emit('cancel')"
      type="button"
      class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-x h-4 w-4"
      >
        <path d="M18 6 6 18"></path>
        <path d="m6 6 12 12"></path>
      </svg>
      <span class="sr-only">Close</span>
    </button>
  </div>
</template>
<script>
import {
  createNotificationRule,
  updateNotificationRule,
  getNotificationRule,
} from "@/api";
import { EventLogLevel, enumToOptions } from "@/enums";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import { listNotificationRecipients, getAllAssetVo } from "@/api";

export default {
  name: "EditWhatsAppAlert",
  components: {
    SmartSelect,
  },
  props: {
    id: {
      type: Number,
      default: null,
    },
    title: {
      type: String,
      default: "",
    },
  },
  computed: {
    EventLogLevel: () => EventLogLevel,
    deviceKeys: {
      get() {
        console.log("Getting deviceKeys:", this.form.deviceKeys);
        if (this.form.deviceKeys == null || this.form.deviceKeys === "") {
          return [];
        }
        return this.form.deviceKeys.split(",");
      },
      set(val) {
        this.form.deviceKeys = val.join(",");
      },
    },
    recipients: {
      get() {
        // console.log("Getting recipientIds:", this.form.recipientIds);
        if (this.form.recipientIds == null || this.form.recipientIds === "") {
          return [];
        }
        return this.form.recipientIds.split(",").map((id) => parseInt(id, 10));
      },
      set(val) {
        this.form.recipientIds = val.join(",");
      },
    },
    escalationRecipients: {
      get() {
        if (
          this.form.escalationRecipientIds == null ||
          this.form.escalationRecipientIds === ""
        ) {
          return [];
        }
        return this.form.escalationRecipientIds
          .split(",")
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id));
      },
      set(val) {
        if (!Array.isArray(val) || !val.length) {
          this.form.escalationRecipientIds = "";
          return;
        }
        this.form.escalationRecipientIds = val.join(",");
      },
    },
    eventLevels: {
      get() {
        // console.log("Getting eventLevels:", this.form.eventLevels);
        if (this.form.eventLevels == null || this.form.eventLevels === "") {
          return [];
        }
        return this.form.eventLevels.split(",").map((id) => parseInt(id, 10));
      },
      set(val) {
        // if (val[0] instanceof Object) {
        //   val = val.map((item) => item.id);
        // }
        // console.log("Setting eventLevels to:", val);
        this.form.eventLevels = val.join(",");
      },
    },
    messagePreview() {
      // 预览示例变量
      let template = this.form.template;
      template = template.replace(/\${eventContent}/g, "Temperature too high");
      template = template.replace(/\${eventLevel}/g, "High");
      template = template.replace(/\${deviceKey}/g, "DVC-001");
      template = template.replace(/\${assetName}/g, "Server Room Sensor");
      template = template.replace(/\${ruleName}/g, "Temperature Alert Rule");
      template = template.replace(/\${eventName}/g, "Temperature Alert");
      template = template.replace(
        /\${timestamp:Asia\/Hong_Kong}/g,
        "2025-12-24 10:30:00"
      );
      template = template.replace(/\${feedbackUrl}/g, "http://example.com/feedback");
      template = template.replace(/\${timestamp}/g, "2025-12-24 10:30:00");
      return template;
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      form: {
        ruleName: "",
        description: "",
        deviceKeys: "",
        eventLevels: "",
        cooldownMin: 15,
        escalationMin: 0,
        escalationRecipientIds: "",
        template: `⚠️ ALERT\n \${eventContent} on \${assetName} (\${deviceKey}) at \${timestamp:Asia/Hong_Kong} \n\${feedbackUrl}`,
        recipientIds: "",
        enabled: false,
      },
    };
  },

  methods: {
    enumToOptions,
    getAllAssetVo,
    listNotificationRecipients,
    async handleSubmit() {
      const payload = {
        ...this.form,
        enabled: this.form.enabled ? 1 : 0,
      };
      try {
        this.submitting = true;
        if (this.id) {
          await updateNotificationRule({ id: this.id, ...payload });
        } else {
          await createNotificationRule(payload);
        }
      } finally {
        this.submitting = false;
      }
      this.$emit("confirm", this.form);
    },
  },
  async mounted() {
    if (this.id) {
      try {
        this.loading = true;
        const rule = await getNotificationRule(this.id);
        Object.assign(this.form, rule);
        this.form.enabled =
          rule.enabled === 1 || rule.enabled === true || rule.enabled === "1";
        console.log("Loaded rule data:", this.form);
      } finally {
        this.loading = false;
      }
    }
  },
};
</script>
